import {
  Alert,
  Box,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {formatNumber} from '../../common/util'
import DateRangePicker from '../../components/DateRangePicker'
import _ from 'lodash'
import {
  useGetAllActiveMerchantsIdAndNameCachedQuery,
  useGetAllOrdersQuery,
} from '../../redux/commonApiCalls'
import Loader from '../../common/Loader'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import {useSnackbar} from 'notistack'
import {useMemo, useState} from 'react'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import StorefrontIcon from '@mui/icons-material/Storefront'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ReplayIcon from '@mui/icons-material/Replay'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SearchIcon from '@mui/icons-material/Search'
import {
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip,
  Legend, ResponsiveContainer,
} from 'recharts'

// ─── Colour palette ────────────────────────────────────────────────────────────
const CHART_COLORS = ['#1565c0','#2e7d32','#6a1b9a','#e65100','#0277bd','#558b2f','#ad1457','#00695c','#f9a825','#4527a0']

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({icon, label, value, sub, color = '#1565c0', bg = '#e3f2fd', highlight = false, info}) {
  return (
    <Box sx={{
      borderRadius: 2.5,
      border: `1px solid ${highlight ? color + '40' : '#e8e8e8'}`,
      backgroundColor: '#fff',
      p: 2,
      height: '100%',
      display: 'flex',
      gap: 1.5,
      alignItems: 'flex-start',
      boxShadow: 'none',
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2,
        backgroundColor: bg, border: `1px solid ${color}25`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Typography sx={{color: '#9e9e9e', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1}}>
            {label}
          </Typography>
          {info && (
            <Tooltip arrow placement='top' title={
              <Box sx={{p: 0.5, maxWidth: 260}}>
                <Typography sx={{fontWeight: 700, fontSize: '0.72rem', mb: 0.5}}>How is this calculated?</Typography>
                <Typography sx={{fontSize: '0.7rem', lineHeight: 1.5}}>{info}</Typography>
              </Box>
            }>
              <IconButton size='small' sx={{p: 0.25, color: '#bdbdbd', '&:hover': {color: '#1565c0'}}}>
                <InfoOutlinedIcon sx={{fontSize: 14}} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography sx={{fontWeight: 800, fontSize: '1.6rem', color: '#1a1a1a', lineHeight: 1.1, mt: 0.4}}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{color: '#9e9e9e', fontSize: '0.7rem', mt: 0.3}}>{sub}</Typography>
        )}
      </Box>
    </Box>
  )
}

// ─── Split Bar ────────────────────────────────────────────────────────────────

function SplitBar({forwardCount, reverseCount, total}) {
  const fwdPct = total > 0 ? Math.round((forwardCount / total) * 100) : 0
  const revPct = 100 - fwdPct
  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.75}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: '#1565c0'}} />
          <Typography sx={{fontSize: '0.75rem', color: '#424242'}}>Forward — {fwdPct}%</Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Box sx={{width: 10, height: 10, borderRadius: '50%', backgroundColor: '#e65100'}} />
          <Typography sx={{fontSize: '0.75rem', color: '#424242'}}>Reverse — {revPct}%</Typography>
        </Box>
      </Box>
      <Box sx={{display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', backgroundColor: '#f0f0f0'}}>
        <Box sx={{width: `${fwdPct}%`, backgroundColor: '#1565c0', transition: 'width 0.6s ease'}} />
        <Box sx={{width: `${revPct}%`, backgroundColor: '#e65100', transition: 'width 0.6s ease'}} />
      </Box>
    </Box>
  )
}

// ─── Chart card wrapper ───────────────────────────────────────────────────────

function ChartCard({title, sub, badge, children}) {
  return (
    <Box sx={{backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)'}}>
      <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0'}}>
        <Box>
          <Typography sx={{fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a'}}>{title}</Typography>
          {sub && <Typography sx={{color: '#9e9e9e', fontSize: '0.75rem', mt: 0.2}}>{sub}</Typography>}
        </Box>
        {badge && (
          <Chip label={badge} size='small' sx={{height: 22, fontSize: '0.68rem', fontWeight: 700, backgroundColor: '#e3f2fd', color: '#1565c0', flexShrink: 0}} />
        )}
      </Box>
      <Box sx={{p: 2.5}}>{children}</Box>
    </Box>
  )
}

// ─── Custom recharts tooltip ──────────────────────────────────────────────────

function ChartTooltip({active, payload, label}) {
  if (!active || !payload?.length) return null
  return (
    <Box sx={{backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1.5, px: 1.5, py: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 140}}>
      <Typography sx={{fontSize: '0.72rem', fontWeight: 700, color: '#757575', mb: 0.5}}>{label}</Typography>
      {payload.map((p) => (
        <Box key={p.name} sx={{display: 'flex', justifyContent: 'space-between', gap: 2}}>
          <Typography sx={{fontSize: '0.75rem', color: p.fill || p.color, fontWeight: 600}}>{p.name}</Typography>
          <Typography sx={{fontSize: '0.75rem', fontWeight: 700, color: '#1a1a1a'}}>{formatNumber(Number(p.value).toFixed(0), 0)}</Typography>
        </Box>
      ))}
    </Box>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

// helper — format Date to YYYY-MM-DD
function toDateStr(d) { return d.toISOString().slice(0, 10) }

export default function Overview() {
  const {enqueueSnackbar} = useSnackbar()
  const [search, setSearch]     = useState('')
  const [page, setPage]         = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [reasonPage, setReasonPage]         = useState(0)
  const [reasonPageSize, setReasonPageSize] = useState(10)

  // ── Date filter — default last 48 hours ──────────────────────────────────────
  const now = useMemo(() => new Date(), [])
  const hrs48Ago = useMemo(() => { const d = new Date(); d.setHours(d.getHours() - 48); return d }, [])
  const [dateRange, setDateRange] = useState([{startDate: hrs48Ago, endDate: now, key: 'selection'}])
  const fromDate = toDateStr(dateRange[0]?.startDate || hrs48Ago)
  const toDate   = toDateStr(dateRange[0]?.endDate || now)

  const {data: allMerchants, isLoading: isMerchantsLoading, isFetching: isMerchantsFetching} =
    useGetAllActiveMerchantsIdAndNameCachedQuery()

  const unprocessedShipmentRequest = useMemo(() => ({
    limit: 10000,
    offset: 0,
    includes: 'id,merchantId,courierDetail,orderType,remark',
    andfilter: [
      {field: 'orderSubStatus', operator: 'eq', value: 'UNPROCESSED'},
      {
        field: 'createdDate',
        operator: 'gte',
        value: `Date('${fromDate} 00:00:00')`,
      },
      {
        field: 'createdDate',
        operator: 'lte',
        value: `Date('${toDate} 23:59:59')`,
      },
      {field: 'merchantId', operator: 'ne', value: 260425082022},
    ],
  }), [fromDate, toDate])

  const {isFetching, isLoading, data, isError, error} = useGetAllOrdersQuery(unprocessedShipmentRequest)

  // ── Derive data (safe when data is undefined before load) ────────────────────
  const allOrders = data?.data || []
  const total = allOrders.length

  const merchantWiseSplit = useMemo(() => _(allOrders)
    .groupBy('merchantId')
    .map((items, merchantId) => {
      const merchant = allMerchants?.find((r) => r?.merchantId === +merchantId)
      const forwardCount = items.filter(
        (i) => i.orderType === 'FORWARD SHIPMENT' || i.order_type === 'FORWARD SHIPMENT'
      ).length
      const reverseCount = items.filter(
        (i) => i.orderType === 'REVERSE SHIPMENT' || i.order_type === 'REVERSE SHIPMENT'
      ).length
      return {
        id: merchant?.merchantId || merchantId,
        name: merchant?.name || 'Unknown',
        forwardCount,
        reverseCount,
        total: items.length,
        pctOfTotal: total > 0 ? +((items.length / total) * 100).toFixed(1) : 0,
      }
    })
    .filter((item) => item.name.toLowerCase() !== 'powerlook')
    .orderBy('total', 'desc')
    .value()
  , [allOrders, allMerchants, total])

  // ── Reason-level aggregation ──────────────────────────────────────────────────
  const reasonWiseSplit = useMemo(() => _(allOrders)
    .groupBy((order) => {
      const remark = order.remark || 'Unknown Reason'
      return remark.length > 100 ? remark.slice(0, 100) + '…' : remark
    })
    .map((items, reason) => ({
      id: reason,
      reason,
      total: items.length,
      pctOfTotal: total > 0 ? +((items.length / total) * 100).toFixed(1) : 0,
    }))
    .orderBy('total', 'desc')
    .value()
  , [allOrders, total])

  // ── Search-filtered rows ─────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return merchantWiseSplit
    return merchantWiseSplit.filter((r) => (r.name || '').toLowerCase().includes(q))
  }, [merchantWiseSplit, search])

  // ── Early returns — after all hooks ─────────────────────────────────────────
  if (isError) enqueueSnackbar(error?.message, {variant: 'error'})
  if (isMerchantsLoading || isMerchantsFetching || isFetching || isLoading) return <Loader />

  // ── Derived aggregates ───────────────────────────────────────────────────────
  const totalForward   = _.sumBy(merchantWiseSplit, 'forwardCount')
  const totalReverse   = _.sumBy(merchantWiseSplit, 'reverseCount')
  const merchantCount  = merchantWiseSplit.filter((m) => m.total > 0).length
  const avgPerMerchant = merchantCount > 0 ? Math.round(total / merchantCount) : 0
  const maxTotal       = merchantWiseSplit[0]?.total || 1

  // ── Chart data ───────────────────────────────────────────────────────────────
  const barChartData = merchantWiseSplit.slice(0, 10).map((m) => ({
    name: m.name,
    Forward: m.forwardCount,
    Reverse: m.reverseCount,
  }))

  const pieData = (() => {
    const top8 = merchantWiseSplit.slice(0, 8)
    const rest = merchantWiseSplit.slice(8)
    const pie  = top8.map((m) => ({name: m.name, value: m.total}))
    if (rest.length) pie.push({name: 'Others', value: _.sumBy(rest, 'total')})
    return pie
  })()

  // ── Columns ─────────────────────────────────────────────────────────────────
  const columns = [
    {
      field: 'name', headerName: 'Merchant', align: 'left', headerAlign: 'left',
      width: 200, sortable: true,
      renderCell: ({row}) => (
        <Tooltip title={row.name} placement='top-start' arrow>
          <Typography sx={{fontSize: '0.82rem', fontWeight: 600, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
            {row.name}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'total', headerName: 'Total', align: 'left', headerAlign: 'center',
      width: 175, sortable: true,
      renderCell: ({row}) => {
        const barPct = maxTotal > 0 ? Math.min((row.total / maxTotal) * 100, 100) : 0
        return (
          <Box sx={{width: '100%', px: 1}}>
            <Box sx={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.4}}>
              <Typography sx={{fontWeight: 700, fontSize: '0.82rem', color: row.total > 50 ? '#c62828' : '#1a1a1a'}}>
                {formatNumber(row.total)}
              </Typography>
              <Typography sx={{fontSize: '0.65rem', color: '#9e9e9e', fontWeight: 600}}>{row.pctOfTotal}%</Typography>
            </Box>
            <LinearProgress
              variant='determinate'
              value={barPct}
              sx={{
                height: 4, borderRadius: 2, backgroundColor: '#f0f0f0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  backgroundColor: row.total > 50 ? '#c62828' : row.total > 20 ? '#e65100' : '#1565c0',
                },
              }}
            />
          </Box>
        )
      },
    },
    {
      field: 'forwardCount', headerName: 'Forward', align: 'center', headerAlign: 'center',
      flex: 1, sortable: true,
      renderCell: ({row}) => (
        <Chip
          label={formatNumber(row.forwardCount)}
          size='small'
          sx={{backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 700, fontSize: '0.75rem', height: 22}}
        />
      ),
    },
    {
      field: 'reverseCount', headerName: 'Reverse', align: 'center', headerAlign: 'center',
      flex: 1, sortable: true,
      renderCell: ({row}) => (
        <Chip
          label={formatNumber(row.reverseCount)}
          size='small'
          sx={{backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 700, fontSize: '0.75rem', height: 22}}
        />
      ),
    },
  ]

  return (
    <Grid container spacing={2}>

      {/* ── Date Filter Row ── */}
      <Grid item xs={12}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          gap: 1.5, flexWrap: 'wrap',
        }}>
          <Chip
            label='Last 48 Hours'
            size='small'
            onClick={() => setDateRange([{startDate: hrs48Ago, endDate: now, key: 'selection'}])}
            sx={{
              fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
              backgroundColor: fromDate === toDateStr(hrs48Ago) ? '#1565c0' : '#e3f2fd',
              color: fromDate === toDateStr(hrs48Ago) ? '#fff' : '#1565c0',
              '&:hover': {backgroundColor: '#1565c0', color: '#fff'},
            }}
          />
          <Chip
            label='Today'
            size='small'
            onClick={() => setDateRange([{startDate: now, endDate: now, key: 'selection'}])}
            sx={{
              fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
              backgroundColor: fromDate === toDate && toDate === toDateStr(now) && fromDate !== toDateStr(hrs48Ago) ? '#1565c0' : '#e3f2fd',
              color: fromDate === toDate && toDate === toDateStr(now) && fromDate !== toDateStr(hrs48Ago) ? '#fff' : '#1565c0',
              '&:hover': {backgroundColor: '#1565c0', color: '#fff'},
            }}
          />
          <DateRangePicker
            defaultRange={dateRange}
            onChange={(r) => setDateRange(r)}
          />
        </Box>
      </Grid>

      {/* ── KPI Row 1 ── */}
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<LocalShippingIcon sx={{fontSize: 22, color: '#c62828'}} />}
          label='Total Unprocessed'
          value={formatNumber(total)}
          sub={`Orders in selected range`}
          color='#c62828' bg='#ffebee'
          highlight={total > 0}
          info='Count of all orders with status "UNPROCESSED" in the selected date range. These are orders that failed to be placed on Proship due to an error. This count resets automatically at 12:00 AM.'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<ArrowForwardIcon sx={{fontSize: 22, color: '#1565c0'}} />}
          label='Forward Unprocessed'
          value={formatNumber(totalForward)}
          sub={total > 0 ? `${Math.round((totalForward / total) * 100)}% of total unprocessed` : '—'}
          color='#1565c0' bg='#e3f2fd'
          info='Count of unprocessed orders where orderType = "FORWARD SHIPMENT" in the selected date range.'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<ReplayIcon sx={{fontSize: 22, color: '#e65100'}} />}
          label='Reverse Unprocessed'
          value={formatNumber(totalReverse)}
          sub={total > 0 ? `${Math.round((totalReverse / total) * 100)}% of total unprocessed` : '—'}
          color='#e65100' bg='#fff3e0'
          info='Count of unprocessed orders where orderType = "REVERSE SHIPMENT" in the selected date range.'
        />
      </Grid>

      {/* ── KPI Row 2 ── */}
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<StorefrontIcon sx={{fontSize: 22, color: '#6a1b9a'}} />}
          label='Merchants Affected'
          value={merchantCount}
          sub='With at least 1 unprocessed order'
          color='#6a1b9a' bg='#f3e5f5'
          info='Count of distinct merchants who have at least one unprocessed order in the selected date range.'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <KpiCard
          icon={<WarningAmberIcon sx={{fontSize: 22, color: '#f57f17'}} />}
          label='Avg per Merchant'
          value={formatNumber(avgPerMerchant)}
          sub='Avg unprocessed orders'
          color='#f57f17' bg='#fff8e1'
          info='Calculated as: Total Unprocessed Orders ÷ Merchants Affected.'
        />
      </Grid>

      {/* ── Split bar ── */}
      {total > 0 && (
        <Grid item xs={12}>
          <Box sx={{backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, p: 2, boxShadow: 'none'}}>
            <Typography sx={{fontWeight: 700, fontSize: '0.8rem', color: '#424242', mb: 1.25}}>
              Forward vs Reverse Split
            </Typography>
            <SplitBar forwardCount={totalForward} reverseCount={totalReverse} total={total} />
          </Box>
        </Grid>
      )}

      {/* ── Alert ── */}
      <Grid item xs={12}>
        <Alert
          severity='info'
          icon={<InfoOutlinedIcon fontSize='small' />}
          sx={{borderRadius: 2, fontSize: '0.8rem', '& .MuiAlert-message': {lineHeight: 1.6}}}
        >
          <strong>Important Notes</strong><br />
          The data below is merchant wise count of unprocessed orders as of now. <strong>Unprocessed orders are orders which could not be placed on Proship due to any error. Proship cleans up all unprocessed orders every day at 12:00 AM.</strong> Once an unprocessed order gets placed, the same is not shown in this dashboard.
        </Alert>
      </Grid>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/*  CHARTS                                                                 */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}

      {merchantWiseSplit.length > 0 && (
        <>
          {/* Stacked bar — top merchants forward/reverse */}
          <Grid item xs={12} md={7}>
            <ChartCard
              title='Top Merchants — Forward vs Reverse'
              sub='Unprocessed orders split by shipment type (top 10 merchants)'
              badge={`${merchantCount} merchants`}
            >
              <ResponsiveContainer width='100%' height={Math.max(220, barChartData.length * 40)}>
                <BarChart
                  data={barChartData}
                  layout='vertical'
                  margin={{top: 0, right: 16, left: 0, bottom: 0}}
                  barSize={14}
                >
                  <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' horizontal={false} />
                  <XAxis type='number' tick={{fontSize: 11, fill: '#9e9e9e'}} axisLine={false} tickLine={false}
                    tickFormatter={(v) => formatNumber(v)} />
                  <YAxis
                    type='category'
                    dataKey='name'
                    width={140}
                    tick={{fontSize: 11, fill: '#424242', width: 130}}
                    axisLine={false}
                    tickLine={false}
                  />
                  <RTooltip content={<ChartTooltip />} />
                  <Legend iconType='circle' iconSize={8} wrapperStyle={{fontSize: 11, paddingTop: 8}} />
                  <Bar dataKey='Forward' stackId='a' fill='#1565c0' fillOpacity={0.85} radius={[0, 0, 0, 0]} />
                  <Bar dataKey='Reverse' stackId='a' fill='#e65100' fillOpacity={0.85} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>

          {/* Donut — merchant share */}
          <Grid item xs={12} md={5}>
            <ChartCard
              title='Merchant Share — Unprocessed'
              sub='Distribution of unprocessed orders by merchant'
              badge={`${total} total`}
            >
              <ResponsiveContainer width='100%' height={Math.max(220, barChartData.length * 30)}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx='50%' cy='50%'
                    innerRadius='46%'
                    outerRadius='70%'
                    paddingAngle={2}
                    dataKey='value'
                    nameKey='name'
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip
                    formatter={(value, name) => [formatNumber(Number(value).toFixed(0), 0), name]}
                    contentStyle={{fontSize: 12, borderRadius: 8, border: '1px solid #e0e0e0'}}
                  />
                  <Legend
                    iconType='circle' iconSize={8}
                    wrapperStyle={{fontSize: 11, paddingTop: 8}}
                    formatter={(v) => v.length > 20 ? v.slice(0, 20) + '…' : v}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/*  TABLE with search + pagination                                         */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      <Grid item xs={12}>
        <Box sx={{backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, overflow: 'hidden', boxShadow: 'none'}}>
          {/* Header */}
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0'}}>
            <Box>
              <Typography sx={{fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a'}}>
                Merchant-wise Unprocessed Orders
              </Typography>
              <Typography sx={{color: '#9e9e9e', fontSize: '0.75rem', mt: 0.2}}>
                {merchantCount} merchant{merchantCount !== 1 ? 's' : ''} affected · Sorted by highest count · Reset daily at 12:00 AM
              </Typography>
            </Box>
            <Chip
              label={`${total} orders`}
              size='small'
              sx={{height: 22, fontSize: '0.68rem', fontWeight: 700, backgroundColor: total > 0 ? '#ffebee' : '#e8f5e9', color: total > 0 ? '#c62828' : '#2e7d32'}}
            />
          </Box>

          {/* Search bar */}
          <Box sx={{px: 2, py: 1.25, borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafbfc', display: 'flex', alignItems: 'center', gap: 2}}>
            <TextField
              size='small'
              placeholder='Search by merchant…'
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0) }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon sx={{fontSize: 17, color: '#9e9e9e'}} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 280,
                '& .MuiOutlinedInput-root': {borderRadius: 2, fontSize: '0.82rem'},
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {borderColor: '#1565c0'},
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: '#1565c0'},
              }}
            />
            {search && (
              <Typography sx={{fontSize: '0.75rem', color: '#9e9e9e'}}>
                {filteredRows.length} result{filteredRows.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          <ProzoDataGrid
            exportToExcel
            rowHeight={42}
            columns={columns}
            rows={filteredRows}
            getRowId={(row) => row.id}
            rowCount={filteredRows.length}
            paginationMode='client'
            pagination
            page={page}
            setPage={setPage}
            sizePerPage={pageSize}
            setSizePerPage={(s) => { setPageSize(s); setPage(0) }}
          />
        </Box>
      </Grid>

      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {/*  REASON-LEVEL VIEW                                                      */}
      {/* ═══════════════════════════════════════════════════════════════════════ */}
      {reasonWiseSplit.length > 0 && (
        <Grid item xs={12}>
          <Box sx={{backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, overflow: 'hidden', boxShadow: 'none'}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0'}}>
              <Box>
                <Typography sx={{fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a'}}>
                  Unprocessed Orders — Reason-Level Breakdown
                </Typography>
                <Typography sx={{color: '#9e9e9e', fontSize: '0.75rem', mt: 0.2}}>
                  {reasonWiseSplit.length} distinct reason{reasonWiseSplit.length !== 1 ? 's' : ''} · Sorted by highest count
                </Typography>
              </Box>
              <Chip
                label={`${total} orders`}
                size='small'
                sx={{height: 22, fontSize: '0.68rem', fontWeight: 700, backgroundColor: '#fff3e0', color: '#e65100'}}
              />
            </Box>
            <ProzoDataGrid
              exportToExcel
              rowHeight={60}
              columns={[
                {
                  field: 'reason', headerName: 'Reason', align: 'left', headerAlign: 'left',
                  flex: 3, sortable: true,
                  renderCell: ({row}) => (
                    <Tooltip title={row.reason} placement='top-start' arrow>
                      <Typography sx={{fontSize: '0.8rem', fontWeight: 600, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal', lineHeight: 1.4}}>
                        {row.reason}
                      </Typography>
                    </Tooltip>
                  ),
                },
                {
                  field: 'total', headerName: 'Count', align: 'center', headerAlign: 'center',
                  width: 120, sortable: true,
                  renderCell: ({row}) => {
                    const maxReasonTotal = reasonWiseSplit[0]?.total || 1
                    const barPct = maxReasonTotal > 0 ? Math.min((row.total / maxReasonTotal) * 100, 100) : 0
                    return (
                      <Box sx={{width: '100%', px: 1}}>
                        <Box sx={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.4}}>
                          <Typography sx={{fontWeight: 700, fontSize: '0.82rem', color: row.total > 20 ? '#c62828' : '#1a1a1a'}}>
                            {formatNumber(row.total)}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant='determinate'
                          value={barPct}
                          sx={{
                            height: 4, borderRadius: 2, backgroundColor: '#f0f0f0',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 2,
                              backgroundColor: row.total > 20 ? '#c62828' : row.total > 5 ? '#e65100' : '#1565c0',
                            },
                          }}
                        />
                      </Box>
                    )
                  },
                },
                {
                  field: 'pctOfTotal', headerName: '% of Total', align: 'center', headerAlign: 'center',
                  width: 110, sortable: true,
                  renderCell: ({row}) => (
                    <Chip
                      label={`${row.pctOfTotal}%`}
                      size='small'
                      sx={{backgroundColor: row.pctOfTotal > 20 ? '#ffebee' : '#e3f2fd', color: row.pctOfTotal > 20 ? '#c62828' : '#1565c0', fontWeight: 700, fontSize: '0.75rem', height: 22}}
                    />
                  ),
                },
              ]}
              rows={reasonWiseSplit}
              getRowId={(row) => row.id}
              rowCount={reasonWiseSplit.length}
              paginationMode='client'
              pagination
              page={reasonPage}
              setPage={setReasonPage}
              sizePerPage={reasonPageSize}
              setSizePerPage={setReasonPageSize}
            />
          </Box>
        </Grid>
      )}
    </Grid>
  )
}
